import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { MessageCircle, X, Send, Loader2, Sparkles, Mic, MicOff } from "lucide-react";
import { API } from "../lib/utils";

const SESSION_KEY = "cowrieking_chat_session";
const STORAGE_KEY = "cowrieking_chat_history";

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    } catch {
      return [];
    }
  });
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY) || null);
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const scrollRef = useRef(null);

  // Voice input via Web Speech API (where supported)
  const supportsVoice = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

  const toggleVoice = () => {
    if (!supportsVoice) return;
    if (listening) {
      recognitionRef.current?.stop();
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const r = new SR();
    r.lang = navigator.language || "en-US";
    r.interimResults = false;
    r.maxAlternatives = 1;
    r.onresult = (ev) => {
      const txt = ev.results[0][0].transcript;
      setInput((curr) => (curr ? `${curr} ${txt}` : txt));
    };
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    recognitionRef.current = r;
    setListening(true);
    r.start();
  };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-50)));
    } catch {}
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    const userMsg = { role: "user", text, ts: Date.now() };
    setMessages((m) => [...m, userMsg]);
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/ai/chat`, { message: text, session_id: sessionId });
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem(SESSION_KEY, data.session_id);
      }
      setMessages((m) => [...m, { role: "assistant", text: data.reply, ts: Date.now() }]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        { role: "assistant", text: "I'm having a brief reach to the studio. Try again in a moment — or visit /contact and we'll respond personally.", ts: Date.now() },
      ]);
    } finally {
      setSending(false);
    }
  };

  return createPortal(
    <>
      <button
        onClick={() => setOpen(!open)}
        data-testid="chatbot-toggle"
        className={`fixed bottom-6 right-6 z-[60] w-14 h-14 flex items-center justify-center transition-all shadow-2xl ${
          open
            ? "bg-[#141210] border border-white/20 text-[#f4f0ec]"
            : "bg-[#d4af37] text-[#0a0908] hover:bg-[#c39f2f]"
        }`}
        aria-label={open ? "Close chat" : "Open chat with Kabutey"}
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      <div
        data-testid="chatbot-panel"
        className={`fixed bottom-24 right-6 z-[60] w-[calc(100vw-3rem)] sm:w-[400px] h-[560px] max-h-[80vh] bg-[#0a0908] border border-white/15 flex flex-col transition-all origin-bottom-right ${
          open ? "scale-100 opacity-100" : "scale-95 opacity-0 pointer-events-none"
        } shadow-2xl`}
      >
        <div className="border-b border-white/10 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 border border-[#d4af37] flex items-center justify-center">
              <Sparkles size={16} className="text-[#d4af37]" />
            </div>
            <div>
              <p className="font-serif text-[#f4f0ec] text-lg leading-tight">Kabutey</p>
              <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#d4af37]">
                Knowledge Guide · Cultural Intelligence
              </p>
            </div>
          </div>
          <button onClick={() => setOpen(false)} className="text-[#a8a29e] hover:text-[#d4af37]">
            <X size={18} />
          </button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
          {messages.length === 0 && (
            <div className="text-center py-8 px-2">
              <p className="font-serif italic text-[#d4af37] text-base leading-relaxed">
                Moyee! Ojekoo, Woezor! Akwaaba.
              </p>
              <p className="font-serif text-[#f4f0ec] text-lg leading-relaxed mt-3">
                I am Kabutey — your Knowledge Guide.
              </p>
              <p className="text-[#a8a29e] text-sm mt-3 leading-relaxed">
                Ask me about culture, history, photography, Ada, Ghana, art, ideas, or wisdom. I am here to help you think and discover.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-2">
                {[
                  "Who are the Ga-Adangme people?",
                  "Tell me about Asafotufiami",
                  "How can I support Eyes of Hope?",
                  "What makes Mershack's photography unique?",
                ].map((s) => (
                  <button
                    key={s}
                    onClick={() => setInput(s)}
                    data-testid={`chat-suggestion-${s.slice(0, 10)}`}
                    className="text-[11px] font-mono uppercase tracking-[0.15em] px-3 py-2 border border-white/15 text-[#a8a29e] hover:border-[#d4af37] hover:text-[#d4af37] transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div
              key={i}
              data-testid={`chat-message-${m.role}`}
              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[85%] px-4 py-3 ${
                  m.role === "user"
                    ? "bg-[#d4af37] text-[#0a0908]"
                    : "bg-[#141210] border border-white/10 text-[#f4f0ec]"
                }`}
              >
                <p className="text-sm whitespace-pre-wrap leading-relaxed">{m.text}</p>
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-[#141210] border border-white/10 px-4 py-3">
                <Loader2 size={16} className="animate-spin text-[#d4af37]" />
              </div>
            </div>
          )}
        </div>

        <form onSubmit={send} className="border-t border-white/10 p-4 flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            data-testid="chat-input"
            placeholder={listening ? "Listening…" : "Ask anything…"}
            className="flex-1 bg-transparent border-b border-white/15 px-1 py-2 text-[#f4f0ec] placeholder:text-[#78716c] focus:border-[#d4af37] focus:outline-none text-sm"
          />
          {supportsVoice && (
            <button
              type="button"
              onClick={toggleVoice}
              data-testid="chat-voice"
              aria-label="Voice input"
              className={`w-10 h-10 flex items-center justify-center transition-colors ${
                listening ? "bg-red-500/20 text-red-300 animate-pulse" : "border border-white/15 text-[#a8a29e] hover:border-[#d4af37] hover:text-[#d4af37]"
              }`}
            >
              {listening ? <MicOff size={16} /> : <Mic size={16} />}
            </button>
          )}
          <button
            type="submit"
            disabled={sending || !input.trim()}
            data-testid="chat-send"
            className="w-10 h-10 bg-[#d4af37] text-[#0a0908] flex items-center justify-center hover:bg-[#c39f2f] disabled:opacity-30 transition-colors"
          >
            <Send size={16} />
          </button>
        </form>
      </div>
    </>,
    document.body
  );
}
